import {
  CreateByFixContractsCompensationsRolloutCompensationEmployeeMapsInput,
  CreateByFixContractsCompensationsRolloutCompensationTemplateInput,
  CreateCompensationsRolloutByFixMutationVariables,
} from 'generated/resources/graphql'

import { amountToNumber, toCents } from 'modules/core/lib/formatCurrencyUtils'

import { UNITS_TYPE_MONEY, UNITS_TYPE_UNITS } from 'modules/contracts/store/models/Compensation'

import {
  CompensationEmployeeMapUI,
  EmployeeMeta,
  LegalEntityMeta,
  PayrollPolicyMeta,
} from '../types'
import { BulkFixContractsAction } from './actions'

export interface WizardData {
  targetEmployees: EmployeeMeta[]
  allowedLegalEntities: LegalEntityMeta[]
  allowedPayrollPolicies: PayrollPolicyMeta[]

  affectedEmployeeIds: Set<number>
  unaffectedEmployeeIds: Set<number>
  keepExistingCompensations: boolean
  compensations: CompensationEmployeeMapUI[]
  multipleLegalEntities: boolean
  multiplePayrollPolicies: boolean
}

export const wizardReducer: React.Reducer<WizardData, BulkFixContractsAction> = (state, action) => {
  switch (action.type) {
    case 'setKeepExistingCompensations': {
      const newState: WizardData = {
        ...state,
        keepExistingCompensations: action.payload,
      }

      return newState
    }
    case 'addCompensation': {
      const includedEmployees = action.payload.employeeIds
      const affectedEmployeeIds = new Set(state.affectedEmployeeIds)
      const unaffectedEmployeeIds = new Set(state.unaffectedEmployeeIds)
      includedEmployees.forEach((eid) => {
        affectedEmployeeIds.add(eid)
        unaffectedEmployeeIds.delete(eid)
      })

      const newState: WizardData = {
        ...state,
        compensations: [action.payload, ...state.compensations],
        affectedEmployeeIds,
        unaffectedEmployeeIds,
      }
      return newState
    }
    case 'updateCompensation': {
      const newCompensations = [...state.compensations]
      newCompensations[action.meta] = action.payload

      // In case the affected employees changed we should recompute
      const { affectedEmployeeIds, unaffectedEmployeeIds } = recalculateAffectedEmployees(
        state,
        newCompensations
      )

      return {
        ...state,
        compensations: newCompensations,
        affectedEmployeeIds,
        unaffectedEmployeeIds,
      }
    }
    case 'removeCompensation': {
      const newCompensations = [...state.compensations]
      newCompensations.splice(action.meta, 1)

      const { affectedEmployeeIds, unaffectedEmployeeIds } = recalculateAffectedEmployees(
        state,
        newCompensations
      )

      return {
        ...state,
        compensations: newCompensations,
        affectedEmployeeIds,
        unaffectedEmployeeIds,
      }
    }
    default:
      return state
  }
}

export const mapToMutationVariables = (
  companyId: number,
  state: WizardData
): CreateCompensationsRolloutByFixMutationVariables => {
  return {
    companyId: companyId,
    compensationEmployeeMaps: state.compensations.map((compensation) => {
      const amount = compensation.compensationTemplate.amount

      const compensationTemplate: CreateByFixContractsCompensationsRolloutCompensationTemplateInput =
        {
          contractsTaxonomyId: compensation.compensationTemplate.contractsTaxonomyId,
          description: compensation.compensationTemplate.description,
          compensationType: compensation.compensationTemplate.compensationType,
          amount: amount ? toCents(amountToNumber(amount)) : null,
          unit:
            compensation.compensationTemplate.unit === UNITS_TYPE_UNITS
              ? UNITS_TYPE_UNITS
              : UNITS_TYPE_MONEY,
          syncWithSupplements: compensation.compensationTemplate.syncWithSupplements,
          recurrenceCount: compensation.compensationTemplate.recurrenceCount,
          startsOn: compensation.compensationTemplate.startsOn,
          recurrence: compensation.compensationTemplate.recurrence,
          firstPaymentOn: compensation.compensationTemplate.firstPaymentOn,
          calculation: compensation.compensationTemplate.calculation,
          timeCondition: compensation.compensationTemplate.timeCondition,
          minimumAmountOfHours: compensation.compensationTemplate.minimumAmountOfHours,
        }

      const compensationEmployeeMap: CreateByFixContractsCompensationsRolloutCompensationEmployeeMapsInput =
        {
          compensationTemplate: compensationTemplate,
          employeeIds: compensation.employeeIds,
        }
      return compensationEmployeeMap
    }),
    removeExistingCompensations: !state.keepExistingCompensations,
  }
}

function recalculateAffectedEmployees(
  state: WizardData,
  newCompensations: CompensationEmployeeMapUI[]
) {
  const unaffectedEmployeeIds: Set<number> = new Set(state.targetEmployees.map((e) => e.id))
  const affectedEmployeeIds: Set<number> = new Set()

  newCompensations
    .flatMap((c) => c.employeeIds)
    .forEach((eid) => {
      affectedEmployeeIds.add(eid)
      unaffectedEmployeeIds.delete(eid)
    })
  return { affectedEmployeeIds, unaffectedEmployeeIds }
}
